import Api from "../../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../../core/services/date-utils";
import {PriceMatrixModifierTypes, PriceMatrixSources, PriceMatrixTypes} from "../../../../../../../components/dialogs/upsert-price-matrix";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer} from "./shared";
import {normalizeSpreadsheetCellValue, toImportCodeUpper, toPartNumberUpper} from "./shared/normalize-spreadsheet-cell-value";

/**
 * Builds the API payload for a row that passed seed + invalidity checks (mirrors legacy success path).
 *
 * @param {*} item
 * @param {Record<string, *>} partIds
 * @param {Map<string, *>} customerCodeToIdMap
 * @param allPriceLevels
 * @param allCategories
 * @param allIndustries
 * @returns {Object|null}
 */
function tryBuildPriceMatrixPayload(item, partIds, customerCodeToIdMap, allPriceLevels, allCategories, allIndustries) {
    const type = Object.values(PriceMatrixTypes).find((t) => t.title.toLowerCase() === item.type?.trim().toLowerCase());
    if (!type) return null;

    let target = null;

    if (type.id !== PriceMatrixTypes.general.id) {
        const targetName = normalizeSpreadsheetCellValue(item.target);
        if (!targetName) {
            if (type.id === PriceMatrixTypes.part.id) {
                return null;
            }
            return null;
        }
        if (type.id === PriceMatrixTypes.part.id) {
            const partId = partIds[toPartNumberUpper(targetName)];
            if (!partId) return null;
            target = {id: partId};
        } else if (type.id === PriceMatrixTypes.category.id) {
            const industry = allIndustries.find((ind) => ind.title.toLowerCase() === item.industry?.trim().toLowerCase());
            if (!industry) return null;
            const category = allCategories.find((c) => c.title.toLowerCase() === targetName.toLowerCase());
            if (!category) return null;
            target = {id: category.id};
        } else if (type.id === PriceMatrixTypes.priceLevel.id) {
            const priceLevel = allPriceLevels.find((p) => p.title?.toLowerCase() === targetName.toLowerCase());
            if (!priceLevel) return null;
            target = {id: priceLevel.id};
        }
    }

    const changeStrRaw = item.change?.trim();
    const isFixedValue = changeStrRaw.toLowerCase().includes("(fixed)");
    const isPercentage = changeStrRaw.includes("%");

    let changeNumericStr = changeStrRaw
        .replace("(Fixed)", "")
        .replace("(fixed)", "")
        .replace("$", "")
        .replace("%", "")
        .replace(",", "")
        .trim();

    const valueParsed = parseFloat(changeNumericStr);
    if (Number.isNaN(valueParsed)) return null;

    let modifier = PriceMatrixModifierTypes.increase;
    let value = valueParsed;
    if (isFixedValue) {
        modifier = PriceMatrixModifierTypes.set;
    } else if (value < 0 || changeNumericStr.startsWith("-")) {
        modifier = PriceMatrixModifierTypes.decrease;
        value = Math.abs(value);
    }

    const basedOnCost = PriceMatrixSources.cost.title.toLowerCase() === item.basedOn?.trim().toLowerCase();
    const startDate = item.startDate?.trim() ? DateUtils.localToServer(item.startDate.trim()) : undefined;
    const endDate = item.endDate?.trim() ? DateUtils.localToServer(item.endDate.trim()) : undefined;

    let customerId;
    if (toImportCodeUpper(item.customerCode)) {
        customerId = customerCodeToIdMap.get(toImportCodeUpper(item.customerCode));
        if (!customerId) return null;
    }

    if (type.id !== PriceMatrixTypes.general.id && !target) return null;

    return {
        part: type.id === PriceMatrixTypes.part.id ? target : null,
        partCategory: type.id === PriceMatrixTypes.category.id ? target : null,
        priceLevel: type.id === PriceMatrixTypes.priceLevel.id ? target : null,
        value: modifier.id === PriceMatrixModifierTypes.decrease.id ? -value : value,
        isFixedValue: modifier.id === PriceMatrixModifierTypes.set.id,
        isPercentage: modifier.id === PriceMatrixModifierTypes.set.id ? false : isPercentage,
        isBasedOnCost: basedOnCost,
        startDate:
            startDate && DateUtils.instance.isDayjs(startDate) && startDate.isValid()
                ? startDate.format(DateUtils.withoutOffsetFormat)
                : undefined,
        endDate:
            endDate && DateUtils.instance.isDayjs(endDate) && endDate.isValid()
                ? endDate.format(DateUtils.withoutOffsetFormat)
                : undefined,
        customer: customerId ? {id: customerId} : undefined,
    };
}

/**
 * Price matrix import — phased validation (see {@link BaseImportTransformer} run contract).
 * `apiRequests` is one entry per CSV row (same length as input); lookups use the shared part cache and
 * parallel customer API calls matching the legacy batch.
 */
export class PriceMatricesImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ allPriceLevels: *, allCategories: *, allIndustries: * }} lists
     */
    async run(mappedData, isFinalRun = false, lists) {
        const {allPriceLevels, allCategories, allIndustries} = lists ?? {};
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, seedValid: boolean, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) continue;
            // required are: type, change, basedOn + (target when type is not general); start/end date and customer code are optional but if present must be valid (validated in later phases)
            if (!rs.item.type?.trim() || !rs.item.change?.toString()?.trim() || !rs.item.basedOn?.trim() || (rs.item.type?.trim().toLowerCase() !== PriceMatrixTypes.general.title.toLowerCase() && !normalizeSpreadsheetCellValue(rs.item.target))) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        const uniquePartNumbers = new Set();
        const uniqueCustomerCodes = new Set();
        for (const item of rows) {
            if (!item) continue;
            const typeId = Object.values(PriceMatrixTypes).find(
                (t) => t.title.toLowerCase() === item.type?.trim().toLowerCase()
            )?.id;
            if (typeId === PriceMatrixTypes.part.id && normalizeSpreadsheetCellValue(item.target)) {
                uniquePartNumbers.add(toPartNumberUpper(item.target));
            }
            if (toImportCodeUpper(item.customerCode)) {
                uniqueCustomerCodes.add(toImportCodeUpper(item.customerCode));
            }
        }

        const partNumbersUpper = [...uniquePartNumbers].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (partNumbersUpper.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(partNumbersUpper);
            partIds = bundle.partIds;
        }

        const customerCodesUpper = [...uniqueCustomerCodes];
        const customerResponses = await Promise.all(
            customerCodesUpper.map((code) => Api.getCustomerSimpleInfoByCode(code, false))
        );

        /** @type {Map<string, *>} */
        const customerCodeToIdMap = new Map();
        customerCodesUpper.forEach((code, index) => {
            const res = customerResponses[index];
            if (res?.resultFlag && res?.data?.id) {
                customerCodeToIdMap.set(code, res.data.id);
            }
        });

        for (const rs of rowState) {
            if (!rs.item) continue;
            const item = rs.item;
            const type = Object.values(PriceMatrixTypes).find((t) => t.title.toLowerCase() === item.type?.trim().toLowerCase());
            if (!!type) {
                if (type.id !== PriceMatrixTypes.general.id) {
                    const targetName = normalizeSpreadsheetCellValue(item.target);
                    if (!targetName) {
                        if (type.id === PriceMatrixTypes.part.id) {
                            rs.codes.push(PreventionInvalidityCode.INVALID_PART);
                        }
                        continue;
                    }
                    if (type.id === PriceMatrixTypes.part.id) {
                        const partId = partIds[toPartNumberUpper(targetName)];
                        if (!partId) rs.codes.push(PreventionInvalidityCode.INVALID_PART);
                    }
                }
            }

            if (toImportCodeUpper(item.customerCode)) {
                const customerId = customerCodeToIdMap.get(toImportCodeUpper(item.customerCode));
                if (!customerId) rs.codes.push(PreventionInvalidityCode.INVALID_CUSTOMER);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || rs.codes.length) continue;
            rs._successPayload = tryBuildPriceMatrixPayload(
                rs.item,
                partIds,
                customerCodeToIdMap,
                allPriceLevels,
                allCategories,
                allIndustries
            );
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                invalidityCodes: [...new Set(rs.codes)],
                originalRow: rs.item,
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, rowIndex) => {
            if (!item) return null;
            if (issueIndexSet.has(rowIndex)) {
                return isFinalRun ? null : {
                    type: item.type,
                    target: item.target,
                    change: item.change,
                    basedOn: item.basedOn,
                    startDate: item.startDate,
                    endDate: item.endDate,
                    customerCode: item.customerCode,
                }
            }
            const rs = rowState[rowIndex];
            if (rs._successPayload !== undefined) {
                return rs._successPayload;
            }
            return null;
        });

        return {apiRequests, preventativeIssues};
    }
}
